import { Link } from "react-router";
import { useState } from "react";
import type { TurnsPage } from "../../lib/admin/types";
import { toDate } from "../../lib/admin/types";
import { RecordingPlayer } from "../audio/RecordingPlayer";
import { RelativeTime } from "../admin/RelativeTime";
import { Chevron } from "../ui/Chevron";

const API_URL = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export type TurnsTableProps = {
  page: TurnsPage;
  /** Server-rendered pager: a URL template containing the literal
   *  string `{page}`, replaced with the page number per link. */
  pageHrefTemplate?: string;
  /** Client-rendered pager: page state lives in the caller. */
  onPageChange?: (page: number) => void;
  /** Auto-expand the most recent turn on first render. */
  defaultOpenLatest?: boolean;
};

function dutchPeek(feedbackMd: string): string | null {
  const [dutch] = feedbackMd.split(/^---\s*$/m);
  const trimmed = (dutch ?? "").trim();
  if (!trimmed) return null;
  const match = trimmed.match(/[^.!?\n]{4,}[.!?]/);
  if (match) return match[0].trim();
  return trimmed.length > 140 ? `${trimmed.slice(0, 140)}…` : trimmed;
}

const CRITERION_MAX = { ad: 3, wo: 2, gr: 2, vl: 2, uit: 3 } as const;
const CRITERION_LABEL: Record<keyof typeof CRITERION_MAX, string> = {
  ad: "Adequaatheid",
  wo: "Woordenschat",
  gr: "Grammatica",
  vl: "Vloeiendheid",
  uit: "Uitspraak",
};

function FeedbackPanel({ feedbackMd }: { feedbackMd: string }) {
  const [dutch, ...rest] = feedbackMd.split(/^---\s*$/m);
  const english = rest.join("---").trim();
  const dutchTrim = (dutch ?? "").trim();
  if (!dutchTrim && !english) return null;
  return (
    <div className="space-y-4">
      {dutchTrim ? (
        <div>
          <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge mb-1">
            Feedback van docent
          </p>
          <p className="font-serif-italic text-base leading-relaxed text-[var(--ink)]">
            {dutchTrim}
          </p>
        </div>
      ) : null}
      {english ? (
        <div>
          <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge mb-1">
            Toelichting
          </p>
          <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--ink-muted)] leading-relaxed border-l-2 border-[var(--ink-faint)]/40 pl-3">
            {english}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function CriteriaRow({
  scores,
}: {
  scores: { ad: number; wo: number; gr: number; vl: number; uit: number };
}) {
  return (
    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
      {(Object.keys(CRITERION_MAX) as (keyof typeof CRITERION_MAX)[]).map((k) => (
        <li
          key={k}
          className="rounded-md bg-[var(--panel-soft)] px-3 py-2 flex items-baseline justify-between gap-2"
        >
          <span className="text-xs text-[var(--ink-muted)]">{CRITERION_LABEL[k]}</span>
          <span className="font-mono-badge text-xs tabular-nums">
            {scores[k]}/{CRITERION_MAX[k]}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function TurnsTable({
  page,
  pageHrefTemplate,
  onPageChange,
  defaultOpenLatest,
}: TurnsTableProps) {
  const buildHref = (n: number): string | null =>
    pageHrefTemplate ? pageHrefTemplate.replace("{page}", String(n)) : null;
  const initialOpen =
    defaultOpenLatest && page.turns.length > 0 ? (page.turns[0]?.turnId ?? null) : null;
  const [openId, setOpenId] = useState<string | null>(initialOpen);
  if (page.total === 0) {
    return (
      <p className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 text-center text-[var(--ink-muted)]">
        Nog geen beurten.
      </p>
    );
  }
  const totalPages = Math.max(1, Math.ceil(page.total / page.pageSize));
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] divide-y divide-[var(--border)] overflow-hidden">
        <div className="grid grid-cols-[auto_auto_1fr_auto_auto] sm:grid-cols-[auto_auto_1fr_1fr_auto_auto] gap-3 px-4 py-3 bg-[var(--panel-soft)] text-[var(--ink-muted)] text-xs uppercase tracking-wider font-mono-badge">
          <span>Datum</span>
          <span>Deel</span>
          <span>Vraag</span>
          <span className="hidden sm:block">Antwoord</span>
          <span className="text-right">Score</span>
          <span aria-hidden />
        </div>
        {page.turns.map((t) => {
          const isOpen = openId === t.turnId;
          const panelId = `turn-${t.turnId}-panel`;
          const peek = t.feedbackMd ? dutchPeek(t.feedbackMd) : null;
          const createdAt = toDate(t.createdAt);
          return (
            <div key={t.turnId}>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : t.turnId)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                aria-label={`Beurt deel ${t.part}, ${t.scoreTotal} van ${t.scoreMax}. ${isOpen ? "Inklappen" : "Uitklappen"}.`}
                className="w-full text-left text-sm hover:bg-[var(--panel-soft)] focus-visible:bg-[var(--panel-soft)] focus-visible:outline-none transition-colors"
              >
                <span className="grid grid-cols-[auto_auto_1fr_auto_auto] sm:grid-cols-[auto_auto_1fr_1fr_auto_auto] gap-3 items-center px-4 pt-3 pb-3">
                  <span className="text-[var(--ink-muted)] whitespace-nowrap text-xs">
                    <RelativeTime when={createdAt} />
                  </span>
                  <span className="inline-flex items-center rounded-md bg-[var(--panel-soft)] px-2 py-0.5 font-mono-badge text-xs">
                    D{t.part}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-serif-italic text-[var(--ink-muted)] truncate">
                      {t.promptText}
                    </span>
                    {peek && !isOpen ? (
                      <span className="block text-xs text-[var(--ink-faint)] truncate italic mt-0.5">
                        {peek}
                      </span>
                    ) : null}
                  </span>
                  <span className="hidden sm:block truncate">{t.userText}</span>
                  <span className="text-right font-mono-badge text-xs tabular-nums">
                    {t.scoreTotal}/{t.scoreMax}
                  </span>
                  <Chevron open={isOpen} />
                </span>
              </button>
              <div
                id={panelId}
                aria-hidden={!isOpen}
                inert={!isOpen ? true : undefined}
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div
                    className={`px-4 pb-5 pt-2 space-y-5 bg-[var(--panel-soft)]/40 transition-opacity duration-200 ${
                      isOpen ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
                          Vraag
                        </p>
                        {t.generatorProvider ? (
                          <ProviderBadge provider={t.generatorProvider} />
                        ) : null}
                      </div>
                      <p className="font-serif-italic text-[var(--ink)] leading-relaxed">
                        {t.promptText}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge mb-1">
                        Antwoord
                      </p>
                      <p className="text-[var(--ink-muted)] leading-relaxed">
                        {t.userText || "(geen verstaanbare spraak)"}
                      </p>
                    </div>
                    {t.audioPath ? (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge mb-2">
                          Opname
                        </p>
                        <RecordingPlayer
                          src={`${API_URL}/audio/${t.audioPath}`}
                          pausedLabel="Beluister antwoord"
                          playingLabel="Pauzeer"
                        />
                      </div>
                    ) : null}
                    {t.scores ? (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge mb-2">
                          Per criterium
                        </p>
                        <CriteriaRow scores={t.scores} />
                      </div>
                    ) : null}
                    {t.feedbackMd ? <FeedbackPanel feedbackMd={t.feedbackMd} /> : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 ? (
        <nav
          aria-label="Pagina"
          className="flex flex-wrap items-center justify-between gap-3 text-sm"
        >
          <p className="text-[var(--ink-muted)]">
            Pagina {page.page} van {totalPages} · {page.total} beurten
          </p>
          <ul className="flex flex-wrap gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => {
              const isCurrent = n === page.page;
              const cls = isCurrent
                ? "inline-flex h-11 min-w-11 sm:h-9 sm:min-w-9 items-center justify-center rounded-md bg-[var(--panel-soft)] px-2 font-semibold text-[var(--ink)]"
                : "inline-flex h-11 min-w-11 sm:h-9 sm:min-w-9 items-center justify-center rounded-md border border-[var(--border)] px-2 text-[var(--ink-muted)] hover:bg-[var(--panel-soft)] hover:text-[var(--ink)]";
              return (
                <li key={n}>
                  {onPageChange ? (
                    <button
                      type="button"
                      onClick={() => onPageChange(n)}
                      aria-current={isCurrent ? "page" : undefined}
                      className={cls}
                    >
                      {n}
                    </button>
                  ) : pageHrefTemplate ? (
                    <Link
                      to={buildHref(n) ?? "#"}
                      aria-current={isCurrent ? "page" : undefined}
                      className={cls}
                    >
                      {n}
                    </Link>
                  ) : (
                    <span aria-current={isCurrent ? "page" : undefined} className={cls}>
                      {n}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}

function ProviderBadge({ provider }: { provider: string }) {
  const label = provider.toUpperCase();
  const tone =
    provider === "claude"
      ? "border-[var(--accent)]/40 text-[var(--ink-muted)]"
      : provider === "gemini"
        ? "border-[var(--warn)]/40 text-[var(--warn)]"
        : provider === "static"
          ? "border-[var(--border)] text-[var(--ink-faint)]"
          : "border-[var(--border)] text-[var(--ink-faint)]";
  return (
    <span
      title={`Cue gegenereerd door ${provider}`}
      className={`inline-flex items-center rounded-md border bg-[var(--panel)] px-1.5 py-0.5 font-mono-badge text-[10px] ${tone}`}
    >
      {label}
    </span>
  );
}
